import React, { useState, useMemo } from 'react';
import { formatDateTime, DATE_TIME } from '@koob/dates';
import { actionColors, actionLabels } from '../constants';
import FieldDiff, { IGNORED_FIELDS, normalizeTimeValue } from './FieldDiff';

function getValueForField(snapshot, field) {
  if (!snapshot || !field) return undefined;

  const parts = field.split('.');
  let value = snapshot;

  for (const part of parts) {
    if (value === undefined || value === null) return undefined;
    value = value[part];
  }

  return value;
}

export default function EntityHistoryCard({
  history,
  fieldLabels = {},
  actionLabelsOverride = {},
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const {
    action,
    beforeSnapshot,
    afterSnapshot,
    changedFields = [],
    userFullName,
    organizationApiKeyName,
    createdAt,
    labels = {},
  } = history;

  // Filter out ignored fields and fields where only time seconds differ
  const filteredChangedFields = useMemo(() => {
    if (!changedFields) return [];
    return changedFields.filter((field) => {
      // Check if this field or its nested path contains an ignored field
      const fieldParts = field.split('.');
      const lastPart = fieldParts[fieldParts.length - 1];

      if (IGNORED_FIELDS.includes(lastPart)) {
        return false;
      }

      // For time fields, check if the difference is only in seconds
      if (lastPart === 'arrivalTime' || lastPart === 'departureTime') {
        const beforeVal = getValueForField(beforeSnapshot, field);
        const afterVal = getValueForField(afterSnapshot, field);
        const normalizedBefore = normalizeTimeValue(beforeVal);
        const normalizedAfter = normalizeTimeValue(afterVal);
        if (normalizedBefore === normalizedAfter) {
          return false;
        }
      }

      return true;
    });
  }, [changedFields, beforeSnapshot, afterSnapshot]);

  const authorName = userFullName || organizationApiKeyName || 'KOOB';
  const color = actionColors[action] || actionColors.update;
  const actionLabel =
    actionLabelsOverride[action] || actionLabels[action] || action;
  const expandable = action === 'update';

  const getFieldLabel = (field) => {
    if (fieldLabels[field]) return fieldLabels[field];

    return field
      .split('.')
      .pop()
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  return (
    <div className="px-5 py-3 rounded-lg bg-gray-50 border">
      <div
        className={[
          'flex justify-between items-center',
          expandable ? 'cursor-pointer' : '',
        ].join(' ')}
        onClick={() => {
          if (!expandable) return;
          setExpanded(!expanded);
        }}
      >
        <div>
          <div className="flex space-x-3 items-center">
            <i className="fa-solid fa-circle text-xs" style={{ color }} />

            <h3
              className="font-semibold tracking-wide capitalize"
              style={{ color }}
            >
              {actionLabel}
            </h3>

            {filteredChangedFields?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {filteredChangedFields.slice(0, 3).map((field) => (
                  <span
                    key={field}
                    className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600"
                  >
                    {getFieldLabel(field)}
                  </span>
                ))}
                {filteredChangedFields.length > 3 && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                    +{filteredChangedFields.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-1 text-gray-500 mt-3">
            <i className="fa-regular fa-user h-4 w-4" />
            <span className="text-sm">{authorName}</span>
          </div>
        </div>

        <div className="flex justify-end items-center space-x-3">
          <span className="text-gray-500 text-sm">
            {formatDateTime(createdAt, DATE_TIME)}
          </span>

          {expandable && (
            <div>
              {expanded ? (
                <i className="fa-regular fa-chevron-up w-5 h-5 text-gray-400" />
              ) : (
                <i className="fa-regular fa-chevron-down w-5 h-5 text-gray-400" />
              )}
            </div>
          )}
        </div>
      </div>

      {expanded && filteredChangedFields.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-[1fr,1fr] gap-x-4 mb-3">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Before
            </h4>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              After
            </h4>
          </div>
          <div className="space-y-3">
            {filteredChangedFields.map((field) => (
              <div
                key={field}
                className="grid grid-cols-[1fr,1fr] gap-x-4 bg-white rounded-2xl border border-solid border-k-gray"
              >
                <div className="p-3 border-r">
                  <FieldDiff
                    fieldName={field}
                    label={getFieldLabel(field)}
                    beforeValue={getValueForField(beforeSnapshot, field)}
                    afterValue={getValueForField(afterSnapshot, field)}
                    labels={labels}
                    viewOnly
                  />
                </div>
                <div className="p-3">
                  <FieldDiff
                    fieldName={field}
                    label={getFieldLabel(field)}
                    beforeValue={getValueForField(beforeSnapshot, field)}
                    afterValue={getValueForField(afterSnapshot, field)}
                    labels={labels}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
